import React, { useState, useEffect, useMemo } from 'react';
import { router } from '@inertiajs/react';
import { Header } from '@/components/Header';
import { BottomNav, DesktopNav } from '@/components/BottomNav';
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Play, Square, Trash2, Zap, TrendingDown, Plus, Layers, ChevronDown, ChevronUp, ScrollText, History, Settings2 } from "lucide-react";
import { store } from '@/store';
import { Provider } from 'react-redux';

export default function StriderBotPage(props: any) {
  return (
    <Provider store={store}>
      <StriderBotContent {...props} />
    </Provider>
  );
}

// ─── 초기 봇 설정 ──────────────────────────────────────────────
const DCA_DEFAULTS = {
  name: 'BTC DCA Hedge',
  symbol: 'BTCUSDT',
  exchange: 'binance',
  strategy_type: 'strider_dca',
  interval: '5m',
  leverage: 3,
  entry_amount: 100,
  is_live: false,
  use_testnet: true,
};

const TSP_DEFAULTS = {
  name: 'ETH TSP Stoch',
  symbol: 'ETHUSDT',
  exchange: 'binance',
  strategy_type: 'tsp_stoch_realtime',
  interval: '15m',
  leverage: 10,
  entry_amount: 100,
  is_live: false,
  use_testnet: true,
};

// ─── 전략 뱃지 색상 ─────────────────────────────────────────────
function strategyColor(type: string) {
  if (type === 'strider_dca') return 'bg-amber-500/15 text-amber-400 border-amber-400/20';
  if (type === 'tsp_stoch_realtime') return 'bg-violet-500/15 text-violet-400 border-violet-400/20';
  return 'bg-muted text-muted-foreground';
}

function strategyLabel(type: string) {
  if (type === 'strider_dca') return 'DCA Hedge';
  if (type === 'tsp_stoch_realtime') return 'TSP Stoch';
  return type;
}

// ─── 봇 카드 컴포넌트 ────────────────────────────────────────────
function BotCard({ bot, onToggle, onLiquidate, onDelete }: any) {
  const [expanded, setExpanded] = useState(false);
  const inPosition = bot.position && bot.position.qty > 0;
  const isActive = bot.is_active;
  const pnlPct = bot.current_pnl_pct ?? 0;
  const trades = bot.trades ?? [];
  const logs = bot.logs ?? [];

  return (
    <Card className="border border-border shadow-sm rounded-2xl overflow-hidden bg-card hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-3 border-b flex flex-row justify-between items-start gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full shrink-0 ${isActive ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground/40'}`} />
            <a href={`/signal/${bot.id}`} className="font-black text-sm text-foreground truncate hover:text-primary hover:underline transition-all">
              {bot.name}
            </a>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className={`text-[9px] font-black px-1.5 py-0 h-4 border ${strategyColor(bot.strategy_type)}`}>
              {strategyLabel(bot.strategy_type)}
            </Badge>
            <span className="text-[10px] text-muted-foreground font-medium">
              {bot.symbol} · {bot.exchange?.toUpperCase()} · {bot.interval}
            </span>
            <span className={`text-[10px] font-bold ${bot.net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              누적: {bot.net_pnl >= 0 ? '+' : ''}{Number(bot.net_pnl || 0).toFixed(2)} USDT
            </span>
          </div>
        </div>
        <Badge variant="outline" className={`shrink-0 text-[9px] font-black px-1.5 h-5 border ${bot.is_live ? (bot.use_testnet ? 'bg-blue-500/10 text-blue-400 border-blue-400/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-400/20') : 'bg-muted/50 text-muted-foreground border-border'}`}>
          {bot.is_live ? (bot.use_testnet ? 'TESTNET' : 'LIVE') : 'SIM'}
        </Badge>
      </CardHeader>

      <CardContent className="p-4 space-y-3">
        {/* 포지션 상태 */}
        {inPosition ? (
          <div className={`p-3 rounded-xl border ${pnlPct >= 0 ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-[10px] font-bold text-muted-foreground">포지션</span>
              <Badge className={`text-[9px] font-black px-1.5 h-4 ${bot.position.side === 'long' ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>
                {bot.position.side?.toUpperCase()}
              </Badge>
            </div>
            <div className="flex justify-between text-[11px]">
              <span className="text-muted-foreground">진입가</span>
              <span className="font-bold">{Number(bot.position.entry_price).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-[11px]">
              <span className="text-muted-foreground">수량</span>
              <span className="font-bold">{bot.position.qty}</span>
            </div>
            <div className={`flex justify-between text-[11px] font-bold ${pnlPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              <span>평가 손익</span>
              <span>{bot.current_pnl >= 0 ? '+' : ''}{Number(bot.current_pnl || 0).toFixed(2)} USDT ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)</span>
            </div>
            {bot.position.entry_time && (
              <div className="flex justify-between text-[10px] text-muted-foreground pt-1 border-t border-border/20 mt-1">
                <span>진입 시간</span>
                <span>{new Date(bot.position.entry_time).toLocaleString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="py-3 text-center text-[11px] text-muted-foreground font-medium bg-muted/20 rounded-xl border border-border/50">
            진입 대기 중
          </div>
        )}

        {/* 최근 상태 */}
        {bot.status && (
          <div className="text-[9px] bg-muted/30 px-2.5 py-1.5 rounded-lg text-muted-foreground font-medium break-all leading-relaxed border border-border/30">
            {bot.status}
          </div>
        )}

        {/* 제어 버튼 */}
        <div className="flex items-center gap-1.5">
          <Button
            onClick={() => onToggle(bot.id)}
            variant={isActive ? 'outline' : 'default'}
            className="flex-1 rounded-xl h-8 text-xs font-bold"
          >
            {isActive ? <><Square className="w-3 h-3 mr-1" />중지</> : <><Play className="w-3 h-3 mr-1" />가동</>}
          </Button>
          {inPosition && (
            <Button onClick={() => onLiquidate(bot.id)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl h-8 px-2.5 text-xs font-bold">
              청산
            </Button>
          )}
          <a
            href={`/signal/${bot.id}`}
            className="inline-flex items-center justify-center text-muted-foreground hover:text-primary rounded-xl h-8 w-8 hover:bg-muted transition-colors"
            title="설정 변경"
          >
            <Settings2 className="w-3.5 h-3.5" />
          </a>
          <Button onClick={() => onDelete(bot.id)} variant="ghost" className="text-muted-foreground hover:text-red-400 rounded-xl h-8 w-8 p-0">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* 상세 보기 접기/펼치기 */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1 pt-1 text-[9px] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? '접기' : `거래 ${trades.length}건 · 로그 ${logs.length}건`}
        </button>

        {expanded && (
          <div className="space-y-2 pt-1 border-t border-border/50">
            {/* 거래 내역 */}
            {trades.length > 0 && (
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <History className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[9px] font-bold text-muted-foreground uppercase">최근 거래</span>
                </div>
                <div className="max-h-32 overflow-y-auto space-y-0.5">
                  {trades.slice(0, 20).map((t: any) => (
                    <div key={t.id} className="flex justify-between items-center text-[9px] bg-muted/20 px-2 py-1 rounded">
                      <span className={`font-bold ${t.pnl > 0 ? 'text-emerald-400' : t.pnl < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                        {t.side?.toUpperCase()} {t.type?.includes('partial') ? '(분할)' : ''}
                      </span>
                      <span className="text-muted-foreground">{Number(t.price).toLocaleString()}</span>
                      <span className={`font-mono font-bold ${t.pnl > 0 ? 'text-emerald-400' : t.pnl < 0 ? 'text-red-400' : ''}`}>
                        {t.pnl > 0 ? '+' : ''}{t.pnl?.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 로그 */}
            {logs.length > 0 && (
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <ScrollText className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[9px] font-bold text-muted-foreground uppercase">최근 로그</span>
                </div>
                <div className="max-h-32 overflow-y-auto space-y-0.5">
                  {logs.slice(0, 30).map((l: any) => (
                    <div key={l.id} className="text-[8px] text-muted-foreground/70 px-2 py-0.5 bg-muted/10 rounded font-mono leading-relaxed">
                      {l.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── 봇 설정 폼 컴포넌트 ─────────────────────────────────────────
function BotConfigForm({ config, onChange, onSubmit, onCancel, title, icon: Icon, accentClass }: any) {
  return (
    <Card className={`border rounded-2xl overflow-hidden ${accentClass}`}>
      <CardHeader className="pb-3 border-b">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <span className="font-black text-sm">{title}</span>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">이름</Label>
            <Input value={config.name} onChange={e => onChange({...config, name: e.target.value})} className="h-8 rounded-lg text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">심볼</Label>
            <Input value={config.symbol} onChange={e => onChange({...config, symbol: e.target.value.toUpperCase()})} className="h-8 rounded-lg text-xs font-mono" />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">레버리지</Label>
            <Input type="number" value={config.leverage} onChange={e => onChange({...config, leverage: parseInt(e.target.value) || 1})} className="h-8 rounded-lg text-xs font-mono" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">진입금 (USDT)</Label>
            <Input type="number" value={config.entry_amount} onChange={e => onChange({...config, entry_amount: parseFloat(e.target.value) || 0})} className="h-8 rounded-lg text-xs font-mono" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">인터벌</Label>
            <Select value={config.interval} onValueChange={v => onChange({...config, interval: v})}>
              <SelectTrigger className="h-8 rounded-lg text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['1m', '3m', '5m', '15m', '30m', '1h'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">거래소</Label>
            <Select value={config.exchange} onValueChange={v => onChange({...config, exchange: v})}>
              <SelectTrigger className="h-8 rounded-lg text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="binance">Binance</SelectItem>
                <SelectItem value="bitget">Bitget</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">모드</Label>
            <Select value={`${config.is_live ? 'live' : 'sim'}_${config.use_testnet ? 'test' : 'main'}`}
              onValueChange={v => {
                const isLive = v.startsWith('live');
                const useTestnet = v.endsWith('test');
                onChange({...config, is_live: isLive, use_testnet: useTestnet});
              }}>
              <SelectTrigger className="h-8 rounded-lg text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sim_test">모의 투자 (Sim)</SelectItem>
                <SelectItem value="live_test">실거래 Testnet</SelectItem>
                <SelectItem value="live_main">실거래 Mainnet</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button variant="ghost" onClick={onCancel} className="text-xs rounded-xl h-8">취소</Button>
          <Button onClick={onSubmit} className="flex-1 text-xs rounded-xl h-8 font-bold">봇 생성</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── 메인 컨텐츠 ─────────────────────────────────────────────────
function StriderBotContent(props: any) {
  const { initial_bots = [], current_user } = props;
  const [bots, setBots] = useState(initial_bots);

  const [dcaConfig, setDcaConfig] = useState({ ...DCA_DEFAULTS });
  const [tspConfig, setTspConfig] = useState({ ...TSP_DEFAULTS });
  const [showDcaForm, setShowDcaForm] = useState(false);
  const [showTspForm, setShowTspForm] = useState(false);
  const [showSetForm, setShowSetForm] = useState(false);

  // 필터 상태 필드 추가
  const [modeFilter, setModeFilter] = useState('all'); // all, live, testnet, simulation
  const [exchangeFilter, setExchangeFilter] = useState('all'); // all, binance, bitget

  useEffect(() => { setBots(initial_bots); }, [initial_bots]);

  useEffect(() => {
    const interval = setInterval(() => {
      router.reload({ only: ['initial_bots'] });
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const toggleBot = (id: number) => {
    const bot = bots.find((b: any) => b.id === id);
    if (!bot) return;
    router.post(`/strider/${id}/${bot.is_active ? 'stop' : 'start'}`, {});
  };

  const handleLiquidate = (id: number) => {
    if (confirm('포지션을 즉시 시장가 청산하시겠습니까?')) {
      router.post(`/strider/${id}/liquidate`, {});
    }
  };

  const handleDelete = (id: number) => {
    if (confirm('이 봇 세션을 삭제하시겠습니까?')) {
      router.delete(`/strider/${id}` as any);
    }
  };

  const submitDca = () => {
    router.post('/strider', { bot: dcaConfig }, {
      onSuccess: () => { setShowDcaForm(false); setShowSetForm(false); }
    });
  };

  const submitTsp = () => {
    router.post('/strider', { bot: tspConfig }, {
      onSuccess: () => { setShowTspForm(false); setShowSetForm(false); }
    });
  };

  const submitSet = () => {
    router.post('/strider', {
      bot: dcaConfig,
      create_set: 'true',
      tsp_leverage: tspConfig.leverage,
      tsp_symbol: tspConfig.symbol,
      tsp_exchange: tspConfig.exchange,
      tsp_interval: tspConfig.interval,
      tsp_entry_amount: tspConfig.entry_amount,
      tsp_is_live: tspConfig.is_live,
      tsp_use_testnet: tspConfig.use_testnet,
      tsp_name: tspConfig.name,
    }, {
      onSuccess: () => setShowSetForm(false)
    });
  };

  // 필터 처리된 봇 목록 계산
  const filteredBots = useMemo(() => {
    return bots.filter((bot: any) => {
      // 거래소 필터링
      if (exchangeFilter !== 'all' && bot.exchange !== exchangeFilter) {
        return false;
      }
      // 모드 필터링
      if (modeFilter === 'live') {
        return bot.is_live && !bot.use_testnet;
      } else if (modeFilter === 'testnet') {
        return bot.is_live && bot.use_testnet;
      } else if (modeFilter === 'simulation') {
        return !bot.is_live;
      }
      return true;
    });
  }, [bots, modeFilter, exchangeFilter]);

  const dcaBots = filteredBots.filter((b: any) => b.strategy_type === 'strider_dca');
  const tspBots = filteredBots.filter((b: any) => b.strategy_type === 'tsp_stoch_realtime');

  return (
    <div className="min-h-screen bg-background font-sans antialiased text-foreground pb-24 md:pb-8">
      <div className="max-w-7xl mx-auto w-full px-4 py-4 md:p-8 space-y-6">
        {/* 헤더 */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <Header />
          <DesktopNav activeTab="strider" setActiveTab={(tab: string) => {
            if (tab === 'dca') window.location.href = '/dca';
            if (tab === 'signal') window.location.href = '/signal';
          }} current_user={current_user} />
        </div>

        {/* 페이지 타이틀 */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
          <div>
            <h1 className="text-2xl font-black tracking-tight flex items-center gap-2.5">
              <Shield className="w-7 h-7 text-primary fill-primary/10" />
              묶음전략 제어판 (유망한 Signal봇 묶음)
            </h1>
            <p className="text-xs text-muted-foreground mt-1 font-medium">
              BTC 급락 DCA 헷지 + ETH TSP 스토캐스틱 타점 세트
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setShowDcaForm(!showDcaForm); setShowTspForm(false); setShowSetForm(false); }} className="rounded-xl text-xs font-bold gap-1.5 border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
              <TrendingDown className="w-3.5 h-3.5" /> DCA 추가
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setShowTspForm(!showTspForm); setShowDcaForm(false); setShowSetForm(false); }} className="rounded-xl text-xs font-bold gap-1.5 border-violet-500/30 text-violet-400 hover:bg-violet-500/10">
              <Zap className="w-3.5 h-3.5" /> TSP 추가
            </Button>
            <Button size="sm" onClick={() => { setShowSetForm(!showSetForm); setShowDcaForm(false); setShowTspForm(false); }} className="rounded-xl text-xs font-bold gap-1.5">
              <Layers className="w-3.5 h-3.5" /> 세트 생성
            </Button>
          </div>
        </div>

        {/* 필터 탐색 바 */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center bg-muted/30 p-2 rounded-2xl border border-border gap-2">
          <div className="flex gap-1 flex-wrap">
            <Button variant={modeFilter === 'all' ? 'default' : 'ghost'} onClick={() => setModeFilter('all')} className="h-8 text-xs rounded-xl font-bold px-3">전체 모드</Button>
            <Button variant={modeFilter === 'live' ? 'default' : 'ghost'} onClick={() => setModeFilter('live')} className="h-8 text-xs rounded-xl font-bold px-3 text-emerald-400">Live (실매매)</Button>
            <Button variant={modeFilter === 'testnet' ? 'default' : 'ghost'} onClick={() => setModeFilter('testnet')} className="h-8 text-xs rounded-xl font-bold px-3 text-blue-400">Testnet</Button>
            <Button variant={modeFilter === 'simulation' ? 'default' : 'ghost'} onClick={() => setModeFilter('simulation')} className="h-8 text-xs rounded-xl font-bold px-3 text-amber-400">Sim (모의)</Button>
          </div>
          <div className="sm:ml-auto flex gap-1 items-center">
            <span className="text-[10px] text-muted-foreground uppercase tracking-widest px-2 font-bold">거래소</span>
            <Select value={exchangeFilter} onValueChange={setExchangeFilter}>
              <SelectTrigger className="h-8 w-28 rounded-xl text-xs bg-card border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체</SelectItem>
                <SelectItem value="binance">Binance</SelectItem>
                <SelectItem value="bitget">Bitget</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* 세트 생성 폼 */}
        {showSetForm && (
          <div className="border border-border rounded-2xl overflow-hidden bg-card animate-in fade-in duration-200">
            <div className="bg-muted/30 px-5 py-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-primary" />
                <span className="font-black text-sm">세트 동시 생성 — DCA (BTC) + TSP (ETH)</span>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-border">
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-1.5 mb-3">
                  <TrendingDown className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs font-black text-amber-400 uppercase tracking-wider">Strider DCA 설정 (BTC)</span>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">이름</Label>
                    <Input value={dcaConfig.name} onChange={e => setDcaConfig({...dcaConfig, name: e.target.value})} className="h-8 rounded-lg text-xs" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">심볼</Label>
                    <Input value={dcaConfig.symbol} onChange={e => setDcaConfig({...dcaConfig, symbol: e.target.value.toUpperCase()})} className="h-8 rounded-lg text-xs font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">레버리지</Label>
                    <Input type="number" value={dcaConfig.leverage} onChange={e => setDcaConfig({...dcaConfig, leverage: parseInt(e.target.value) || 1})} className="h-8 rounded-lg text-xs font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">진입금 (USDT)</Label>
                    <Input type="number" value={dcaConfig.entry_amount} onChange={e => setDcaConfig({...dcaConfig, entry_amount: parseFloat(e.target.value) || 0})} className="h-8 rounded-lg text-xs font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">거래소</Label>
                    <Select value={dcaConfig.exchange} onValueChange={v => setDcaConfig({...dcaConfig, exchange: v})}>
                      <SelectTrigger className="h-8 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="binance">Binance</SelectItem>
                        <SelectItem value="bitget">Bitget</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">모드</Label>
                    <Select value={`${dcaConfig.is_live ? 'live' : 'sim'}_${dcaConfig.use_testnet ? 'test' : 'main'}`}
                      onValueChange={v => setDcaConfig({...dcaConfig, is_live: v.startsWith('live'), use_testnet: v.endsWith('test')})}>
                      <SelectTrigger className="h-8 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sim_test">모의 투자</SelectItem>
                        <SelectItem value="live_test">Testnet</SelectItem>
                        <SelectItem value="live_main">Mainnet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-1.5 mb-3">
                  <Zap className="w-3.5 h-3.5 text-violet-400" />
                  <span className="text-xs font-black text-violet-400 uppercase tracking-wider">TSP Stoch 설정 (ETH)</span>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">이름</Label>
                    <Input value={tspConfig.name} onChange={e => setTspConfig({...tspConfig, name: e.target.value})} className="h-8 rounded-lg text-xs" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">심볼</Label>
                    <Input value={tspConfig.symbol} onChange={e => setTspConfig({...tspConfig, symbol: e.target.value.toUpperCase()})} className="h-8 rounded-lg text-xs font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">레버리지</Label>
                    <Input type="number" value={tspConfig.leverage} onChange={e => setTspConfig({...tspConfig, leverage: parseInt(e.target.value) || 1})} className="h-8 rounded-lg text-xs font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">진입금 (USDT)</Label>
                    <Input type="number" value={tspConfig.entry_amount} onChange={e => setTspConfig({...tspConfig, entry_amount: parseFloat(e.target.value) || 0})} className="h-8 rounded-lg text-xs font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">거래소</Label>
                    <Select value={tspConfig.exchange} onValueChange={v => setTspConfig({...tspConfig, exchange: v})}>
                      <SelectTrigger className="h-8 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="binance">Binance</SelectItem>
                        <SelectItem value="bitget">Bitget</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-muted-foreground">모드</Label>
                    <Select value={`${tspConfig.is_live ? 'live' : 'sim'}_${tspConfig.use_testnet ? 'test' : 'main'}`}
                      onValueChange={v => setTspConfig({...tspConfig, is_live: v.startsWith('live'), use_testnet: v.endsWith('test')})}>
                      <SelectTrigger className="h-8 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sim_test">모의 투자</SelectItem>
                        <SelectItem value="live_test">Testnet</SelectItem>
                        <SelectItem value="live_main">Mainnet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t bg-muted/20 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowSetForm(false)} className="text-xs rounded-xl h-8">취소</Button>
              <Button onClick={submitSet} className="text-xs rounded-xl h-8 font-bold px-6">
                <Layers className="w-3.5 h-3.5 mr-1.5" /> 세트 동시 생성
              </Button>
            </div>
          </div>
        )}

        {/* DCA 단독 추가 폼 */}
        {showDcaForm && (
          <BotConfigForm
            config={dcaConfig}
            onChange={setDcaConfig}
            onSubmit={submitDca}
            onCancel={() => setShowDcaForm(false)}
            title="Strider DCA 봇 설정"
            icon={TrendingDown}
            accentClass="border-amber-500/30 bg-amber-500/5"
          />
        )}

        {/* TSP 단독 추가 폼 */}
        {showTspForm && (
          <BotConfigForm
            config={tspConfig}
            onChange={setTspConfig}
            onSubmit={submitTsp}
            onCancel={() => setShowTspForm(false)}
            title="TSP Stoch Realtime 봇 설정"
            icon={Zap}
            accentClass="border-violet-500/30 bg-violet-500/5"
          />
        )}

        {/* ── DCA 봇 섹션 ────────────────────────── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-black text-amber-400 uppercase tracking-wider">Strider DCA (BTC 급락 분할매수)</h2>
            <Badge variant="outline" className="text-[9px] font-bold border-amber-400/20 text-amber-400 bg-amber-400/10">{dcaBots.length}개</Badge>
          </div>
          {dcaBots.length === 0 ? (
            <div
              onClick={() => setShowDcaForm(true)}
              className="border-2 border-dashed border-amber-500/20 rounded-2xl p-8 text-center text-xs text-muted-foreground font-medium cursor-pointer hover:border-amber-500/40 hover:bg-amber-500/5 transition-all"
            >
              <Plus className="w-5 h-5 mx-auto mb-2 text-amber-400/60" />
              DCA 봇이 없습니다. 클릭하여 추가하세요.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dcaBots.map((bot: any) => (
                <BotCard key={bot.id} bot={bot} onToggle={toggleBot} onLiquidate={handleLiquidate} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>

        {/* ── TSP 봇 섹션 ────────────────────────── */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-violet-400" />
            <h2 className="text-sm font-black text-violet-400 uppercase tracking-wider">TSP Stochastic Realtime (ETH 타점)</h2>
            <Badge variant="outline" className="text-[9px] font-bold border-violet-400/20 text-violet-400 bg-violet-400/10">{tspBots.length}개</Badge>
          </div>
          {tspBots.length === 0 ? (
            <div
              onClick={() => setShowTspForm(true)}
              className="border-2 border-dashed border-violet-500/20 rounded-2xl p-8 text-center text-xs text-muted-foreground font-medium cursor-pointer hover:border-violet-500/40 hover:bg-violet-500/5 transition-all"
            >
              <Plus className="w-5 h-5 mx-auto mb-2 text-violet-400/60" />
              TSP 봇이 없습니다. 클릭하여 추가하세요.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tspBots.map((bot: any) => (
                <BotCard key={bot.id} bot={bot} onToggle={toggleBot} onLiquidate={handleLiquidate} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomNav activeTab="strider" setActiveTab={(tab: string) => {
        if (tab === 'dca') window.location.href = '/dca';
        if (tab === 'signal') window.location.href = '/signal';
      }} current_user={current_user} />

      {current_user?.logout_path && (
        <div className="max-w-md mx-auto mt-4 pb-12 flex flex-col items-center gap-1 opacity-40 hover:opacity-100 transition-opacity">
          {(current_user.name || current_user.email) && (
            <a
              href="/dca#profile"
              className="text-[10px] font-bold text-muted-foreground/60 hover:text-primary transition-colors tracking-tight"
            >
              {current_user.name ? `${current_user.name} (${current_user.email})` : current_user.email}
            </a>
          )}
          <div className="flex items-center gap-3">
            {current_user.is_admin && (
              <a
                href="/admin"
                className="text-[10px] font-black text-muted-foreground/60 hover:text-primary transition-colors uppercase tracking-widest flex items-center gap-1"
              >
                <Shield className="w-3 h-3" /> /admin
              </a>
            )}
            {current_user.is_impersonated && (
              <a
                href="?user_id=clear"
                className="text-[11px] font-black text-amber-500 hover:text-amber-600 transition-colors uppercase tracking-widest"
              >
                돌아가기 (Exit Impersonation)
              </a>
            )}
            <button
              type="button"
              onClick={() => router.delete(current_user.logout_path)}
              className="text-[11px] font-black text-muted-foreground/80 hover:text-danger transition-colors uppercase tracking-widest"
            >
              Sign Out / 로그아웃
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
